import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { PlayerIndex, ScoreState } from '@aiscoreboard/contracts';
import { useT } from '../lib/i18n';

interface Props {
  players: ScoreState['players'];
  disabled: boolean;
  error: string | null;
  onSelect: (player: PlayerIndex) => void;
  onCancel: () => void;
}

export function FrameWinnerDialog({ players, disabled, error, onSelect, onCancel }: Props) {
  const t = useT();
  const titleId = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => element?.close();
  }, []);

  return createPortal(
    <dialog ref={dialog} aria-labelledby={titleId}
      onCancel={event => { event.preventDefault(); if (!disabled) onCancel(); }}
      className="panel fixed inset-0 m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%_-_2rem)] max-w-sm overflow-y-auto border-0 bg-hud-bg/90 p-4 text-white backdrop:bg-black/75">
      <div className="flex flex-col gap-4">
        <h2 id={titleId} className="text-center text-lg font-bold">{t('match.whoWon')}</h2>
        <p className="text-center text-sm text-white/70">{t('match.frameTied')}</p>
        <div className="grid grid-cols-2 gap-2">
          {([0, 1] as const).map(player => (
            <button key={player} type="button" disabled={disabled}
              onClick={() => onSelect(player)}
              className="btn min-h-11 min-w-0 break-words bg-white px-3 text-hud-bg">
              {players[player]}
            </button>
          ))}
        </div>
        {error && <p role="alert" className="text-center text-sm text-sys-red">{error}</p>}
        <button type="button" disabled={disabled} onClick={onCancel} className="btn-ghost min-h-11">
          {t('common.cancel')}
        </button>
      </div>
    </dialog>,
    document.body,
  );
}
